import { useEffect, useState } from 'react';
import { SubmitHandler, UseFormReturn } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import TextField from 'components/common/text-field';
import TextFieldPhone from 'components/common/text-field-phone';
import { IGoogleData } from 'components/google-places/useGooglePlaces';
import { useWindow } from 'hooks/useWindow';
import { Fields } from 'types/fields';
import messages from '../messages';
import GoogleAutocomplete from './googleAutocomplete';
import {
  AddressInputWrapper,
  BackCTA,
  Disclaimer,
  FormHeader,
  HomeValuationForm,
  NameInput,
  SelectedAddress,
  SubmitButton,
  ValuationFormWrapper,
} from './styles';
import ThankYouMessage from './thankYouMessage';
import ValuationPriceMobile from './valuationPriceMobile';

interface ValuationFormProps {
  onSubmit: SubmitHandler<Fields>;
  widgetError: boolean;
  price: string | null;
  loading: boolean;
  hasWidgetLoaded: boolean;
  setGoogleData: (googleData: IGoogleData & { full: string }) => void;
  scrollToWidget: () => void;
  homeValuationForm: UseFormReturn<Fields>;
  address: string;
  onBack: () => void;
}

function ValuationForm({
  onSubmit,
  widgetError,
  price,
  loading,
  hasWidgetLoaded,
  setGoogleData,
  scrollToWidget,
  homeValuationForm,
  address,
  onBack,
}: ValuationFormProps) {
  const [showDetailButton, setShowDetailButton] = useState(false);
  const { w: width } = useWindow();

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    clearErrors,
    setError,
    trigger,
    getValues,
  } = homeValuationForm;

  const timeoutDuration = width > 768 ? 0 : 2000;

  useEffect(() => {
    if (widgetError) {
      setError('address', {
        type: 'custom',
        message:
          'We could not understand the property address you entered. Please make sure you enter a full property address.',
      });
    }
  }, [widgetError, setError]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (price) {
      timer = setTimeout(() => {
        setShowDetailButton(true);
      }, timeoutDuration);
    } else {
      setShowDetailButton(false);
    }

    return () => clearTimeout(timer);
  }, [price, timeoutDuration]);

  return (
    <ValuationFormWrapper>
      <ValuationPriceMobile
        price={price}
        scrollToWidget={scrollToWidget}
        showDetailButton={showDetailButton}
      />
      {hasWidgetLoaded && showDetailButton ? (
        <ThankYouMessage />
      ) : (
        <>
          <FormHeader>
            <FormattedMessage
              {...messages.formHeader}
              values={{ rsquo: <>&rsquo;</> }}
            />
          </FormHeader>
          <SelectedAddress>{address}</SelectedAddress>
          <HomeValuationForm onSubmit={handleSubmit(onSubmit)}>
            <AddressInputWrapper>
              <GoogleAutocomplete
                setValue={setValue}
                clearErrors={clearErrors}
                name="address"
                error={errors.address}
                setGoogleData={setGoogleData}
                trigger={trigger}
                defaultValue={getValues('address')}
              />
            </AddressInputWrapper>
            <NameInput>
              <TextField
                type="text"
                name="first_name"
                register={register}
                placeholder="First Name"
                error={errors.first_name}
              />
              <TextField
                type="text"
                name="last_name"
                register={register}
                placeholder="Last Name"
                error={errors.last_name}
              />
            </NameInput>
            <TextField
              type="email"
              name="email"
              register={register}
              placeholder="Email"
              error={errors.email}
            />
            <TextFieldPhone
              name="phone_number"
              control={control}
              error={errors.phone_number}
            />
            <SubmitButton type="submit" isLoading={loading} disabled={loading}>
              {loading ? (
                <span className="btn-ring" />
              ) : (
                <FormattedMessage {...messages.submitButton} />
              )}
            </SubmitButton>
            <Disclaimer>
              <FormattedMessage {...messages.disclaimer} />
            </Disclaimer>
            <BackCTA type="button" onClick={onBack}>
              <FormattedMessage {...messages.backCTA} />
            </BackCTA>
          </HomeValuationForm>
        </>
      )}
    </ValuationFormWrapper>
  );
}

export default ValuationForm;
